import { Component, h, Prop, Element, Listen } from '@stencil/core';

/**
 * COMPNAME creates a wrapper for the modal component that auto-launches on page load.
 *
 * When a user closes the modal, a cookie is set to specify a duration, in days, for waiting to load the interstitial again.
 *
 * The cookie will disappear on expiration, so its existence is what determines whether to launch the modal or not.
 */

@Component({
  tag: 'aafp-interstitial',
  shadow: false,
})
export class AafpInterstitial {
  /**
   * For setting the unique identifier of the modal within the interstitial
   */
  @Prop() modalId!: string;
  /**
   * For setting the heading of the modal
   */
  @Prop() modalHeading: string;
  /**
   * The AEM fragment path, saved as the cookie value
   */
  @Prop() fragmentPath!: string;
  /**
   * Number of days before cookie expires
   */
  @Prop({ mutable: true }) expirationDays: number = 1;
  /**
   * Configures correct behavior / display depending on the wcmmode in AEM. In edit or design mode the modal content will display inline.
   */
  @Prop({ mutable: true }) wcmmode: 'edit' | 'design' | 'preview' | 'analytics' | 'read_only' | 'disabled' = 'disabled';

  @Element() element: HTMLElement;

  modalRef!: HTMLAafpModalElement;

  /**
   * Listen for the modal to emit an event on close
   * @param modal
   */
  @Listen('modalHidden')
  modalHiddenHandler(modal: CustomEvent) {
    if (modal.detail === this.modalId) {
      this.onModalClose();
    }
  }

  /**
   * Run close functionality on modal link
   * @param event
   */
  @Listen('click', { target: 'window' })
  modalClickHandler(event: any) {
    if (
      event.target.closest('.adl-modal__container') !== null &&
      (event.target.closest('a') || event.target.closest('button'))
    ) {
      this.onModalClose();
    }
  }

  componentDidLoad() {
    if (this.wcmmode === 'edit' || this.wcmmode === 'design') {
      return;
    }

    // if a matching cookie does not exist (or we are in preview mode)
    // we can launch the modal
    if (
      (!this.cookieExists(this.modalId) || this.wcmmode === 'preview') &&
      typeof this.modalRef.showModal === 'function'
    ) {
      this.modalRef.showModal(this.modalId).then();
    }
  }

  /**
   * Actions for when the modal is closed
   */
  onModalClose() {
    if (this.wcmmode === 'disabled') {
      this.setCookie(this.modalId, this.fragmentPath, this.expirationDays);
    }
  }

  /**
   * Set the cookie that acts as a gatekeeper for the modal
   *
   * @param cookieName
   * @param cookieValue
   * @param expDays
   */

  setCookie(cookieName: string, cookieValue: string, expDays: number) {
    let date = new Date();
    date.setTime(date.getTime() + expDays * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + date.toUTCString();
    document.cookie = cookieName + '=' + cookieValue + '; ' + expires + '; path=/';
  }

  deleteCookie(path: string, domain: string) {
    document.cookie =
      this.modalId +
      '=' +
      (path ? ';path=' + path : '') +
      (domain ? ';domain=' + domain : '') +
      ';expires=Thu, 01 Jan 1970 00:00:01 GMT';
  }

  /**
   * Check to see if the specified cookie exists
   * @param cookieName
   */
  cookieExists(cookieName: string) {
    const myReg = new RegExp('^(.*;)?\\s*' + cookieName + '\\s*=\\s*[^;]+(.*)?$');
    return document.cookie.match(myReg);
  }

  render() {
    return (
      <div class="adl-interstitial">
        <aafp-modal
          actionId={this.modalId}
          heading={this.modalHeading}
          modalType="basic"
          showClose={true}
          wcmmode={this.wcmmode}
          ref={(el) => (this.modalRef = el as HTMLAafpModalElement)}
        >
          <div slot="content">
            <slot />
          </div>
        </aafp-modal>
      </div>
    );
  }
}
